"use client"

import { useLocation, useNavigate } from "react-router"

import {
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu"
import { useProduct } from "@/contexts/product-context"
import { resolveDashboardDemoMode } from "@/lib/evaluation-dashboard-context"
import {
  demoModeToScenario,
  EVALUATION_DASHBOARD_SCENARIOS,
  evaluationDashboardScenarioLabel,
  scenarioToDemoMode,
  type EvaluationDashboardScenario,
} from "@/lib/evaluation-dashboard-scenario"
import { evaluationDashboardPath } from "@/lib/evaluation-routes"
import {
  setEvaluationDashboardScenario,
  useEvaluationDashboardScenario,
} from "@/hooks/use-evaluation-dashboard-scenario"

function ScenarioRadioItem({
  label,
  description,
  value,
}: {
  label: string
  description: string
  value: EvaluationDashboardScenario
}) {
  return (
    <DropdownMenuRadioItem value={value} className="items-start gap-2 py-2">
      <span className="flex flex-col gap-0.5">
        <span className="font-medium">{label}</span>
        <span className="text-xs leading-snug text-muted-foreground">{description}</span>
      </span>
    </DropdownMenuRadioItem>
  )
}

export function EvaluationDashboardScenarioMenu() {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const { product } = useProduct()
  const scenario = useEvaluationDashboardScenario()
  const effectiveScenario = demoModeToScenario(resolveDashboardDemoMode(search, scenario))
  const activeLabel = evaluationDashboardScenarioLabel(effectiveScenario)

  const selectScenario = (next: EvaluationDashboardScenario) => {
    setEvaluationDashboardScenario(next)
    if (product !== "exxat-surveys") return

    const dashboardPath = evaluationDashboardPath(product)
    if (pathname !== dashboardPath) return

    const demo = scenarioToDemoMode(next)
    const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search)
    params.delete("demo")
    if (demo) params.set("demo", demo)

    const nextSearch = params.toString()
    navigate(
      { pathname: dashboardPath, search: nextSearch ? `?${nextSearch}` : "" },
      { replace: true },
    )
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <i className="fa-light fa-layer-group" aria-hidden="true" />
        Dashboard scenario
        <span className="ms-auto pe-1 text-xs text-muted-foreground">{activeLabel}</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="w-64">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          Mock data for demo previews
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={effectiveScenario}
          onValueChange={value => selectScenario(value as EvaluationDashboardScenario)}
        >
          {EVALUATION_DASHBOARD_SCENARIOS.map(option => (
            <ScenarioRadioItem
              key={option.id}
              value={option.id}
              label={option.label}
              description={option.description}
            />
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  )
}
